import { cookieConsentDefaultValue } from '@utils/Cookies';
import { trackCustomEvent } from '@utils/statistics/clientStatistics';
import { CookieConsentAction, trackCookieConsentAction } from '@utils/statistics/customEvents';
import { CookieConsentContext } from 'app/components/CookieConsent/CookieConsentProvider';
import { CookieConsentType } from 'app/components/CookieConsent/CookieConsentType';
import { useContext, useEffect, useState } from 'react';
import { SubPage, Variant } from '../CookieConsent';
import { CookieContextValue } from '../CookieConsentProvider';

const CONFIRMATION_TIMEOUT = 1500;

export const useCookieConsent = () => {
    const { values, setValues, variant, cookieExists, isOptedIn, showConsentModal, setShowConsentModal } =
        useContext(CookieConsentContext);
    const [checked, setChecked] = useState<CookieContextValue>(values);
    const [currentPage, setCurrentPage] = useState<SubPage>(
        variant === Variant.OneLayer || showConsentModal.expanded ? SubPage.Settings : SubPage.Info
    );

    // Callback for switch inputs
    const onChecked = async (isChecked: boolean, type: CookieConsentType) => {
        setChecked({ ...checked, [type]: isChecked });
    };

    // Initialize the first page
    const setFirstPage = () => {
        const firstPage: SubPage =
            variant === Variant.OneLayer || variant === Variant.OneLayerRejectAll
                ? SubPage.Settings
                : SubPage.Info;
        setCurrentPage(firstPage);
    };

    // Save custom settings
    const save = () => {
        trackCustomEvent(trackCookieConsentAction(currentPage, CookieConsentAction.CUSTOM, checked));

        setCurrentPage(SubPage.Confirm);

        setTimeout(() => {
            setValues(checked);
            setShowConsentModal({ visible: false, expanded: false });
            setFirstPage();
        }, CONFIRMATION_TIMEOUT);
    };

    // Reject all cookies
    const rejectAll = () => {
        const result = cookieConsentDefaultValue(false);

        setCurrentPage(SubPage.Confirm);

        setTimeout(() => {
            setValues(result);
            trackCustomEvent(trackCookieConsentAction(SubPage.Info, CookieConsentAction.REJECT_ALL, result));
            setShowConsentModal({ visible: false, expanded: false });
            setFirstPage();
        }, CONFIRMATION_TIMEOUT);
    };

    // Accept all cookies
    const acceptAll = () => {
        const result = cookieConsentDefaultValue(true);

        setCurrentPage(SubPage.Confirm);

        setTimeout(() => {
            setValues(result);
            trackCustomEvent(trackCookieConsentAction(currentPage, CookieConsentAction.ACCEPT_ALL, result));
            setShowConsentModal({ visible: false, expanded: false });
            setFirstPage();
        }, CONFIRMATION_TIMEOUT);
    };

    // Initialize first page based on Abisko allocation
    useEffect(() => {
        setFirstPage();
    }, [variant]);

    // Set consent dialoge visibility based on:
    //  - if the cookie exists
    //  - The Abisko allocation
    useEffect(() => {
        const visible = variant !== Variant.Original && !cookieExists;

        setShowConsentModal({ visible, expanded: false });
    }, [cookieExists, variant]);

    return [
        currentPage,
        setCurrentPage,
        save,
        acceptAll,
        rejectAll,
        onChecked,
        isOptedIn,
        showConsentModal,
    ] as const;
};
